import { FileDown, Rocket, ShieldAlert, ShieldCheck } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { useCheckout } from "@/context/CheckoutContext";
import { ReservationNotice } from "@/components/iv/concept/ReservationNotice";
import { downloadReport, usd, usePurchases } from "@/lib/purchases";

const Row = ({ k, v, testId }) => (
  <div className="flex flex-col gap-0.5 border-t border-white/5 py-2 text-sm sm:flex-row sm:justify-between sm:gap-3">
    <dt className="text-slate-400">{k}</dt><dd className="font-semibold text-white sm:text-right" data-testid={testId}>{v}</dd>
  </div>
);

export const CostAnchor = ({ slug, reservation, evaluationId }) => {
  const { priceFor } = useApp();
  const { openCheckout } = useCheckout();
  const owned = usePurchases(evaluationId);
  const dfy = priceFor("DFY_WEBSITE_LAUNCH");
  const hostMinor = dfy?.optional_monthly_hosting_minor;
  const hosting = hostMinor ? `$${(hostMinor / 100).toLocaleString("en-US")}/mo` : "—";
  return (
    <section className="grid gap-4 lg:grid-cols-12" data-testid="cost-anchor-section">
      <div className="min-w-0 space-y-4 lg:col-span-8">
        <h2 className="text-lg font-bold text-white">Traditional Dev Agency vs. IdeaValidator360 — Cost Anchor Matrix</h2>
        <article className="rounded-2xl border border-rose-400/40 bg-rose-500/[0.06] p-4" data-testid="cost-agency-card">
          <h3 className="flex items-center gap-2 font-bold text-rose-200"><ShieldAlert className="h-4 w-4" /> 1 · Traditional Custom Dev Agency</h3>
          <dl className="mt-2">
            <Row k="Build cost" v="$5,000–$12,000" testId="cost-agency-build" />
            <Row k="Launch timeline" v="6 to 8 Weeks" testId="cost-agency-timeline" />
            <Row k="Maintenance" v="$150–$300/mo" testId="cost-agency-maintenance" />
            <Row k="Risk" v="High Risk (Huge Upfront Sunk Cost)" testId="cost-agency-risk" />
          </dl>
        </article>
        <article className="rounded-2xl border border-emerald-400/50 bg-emerald-500/[0.06] p-4 shadow-[0_0_30px_rgba(16,185,129,0.12)]" data-testid="cost-iv360-card">
          <h3 className="flex flex-wrap items-center gap-2 font-bold text-emerald-200"><ShieldCheck className="h-4 w-4" /> 2 · IdeaValidator360 Instant Builder
            <span className="rounded-full bg-emerald-400/20 px-2 py-0.5 text-[10px] text-emerald-200">Recommended</span></h3>
          <dl className="mt-2">
            <Row k="Build cost" v={`$0 (Test Preview) or ${usd(dfy)} (Live Custom Domain DFY Build)`} testId="cost-iv360-build" />
            <Row k="Launch timeline" v="Instant Preview / 48 Hours Live" testId="cost-iv360-timeline" />
            <Row k="Managed hosting" v={hosting} testId="cost-iv360-hosting" />
            <Row k="Risk" v="Zero Risk (Cancel Anytime)" testId="cost-iv360-risk" />
          </dl>
        </article>
      </div>
      <aside className="iv-glass min-w-0 space-y-3 rounded-2xl p-4 lg:col-span-4" data-testid="founder-window-card">
        <h3 className="font-bold text-white">Founder Execution Window</h3>
        <p className="text-sm text-slate-400" data-testid="founder-window-subtitle">{reservation?.expired ? "Your test preview has closed, but your full report is still in your workspace. Relaunch it on your own domain any time." : "Your free test site is live now. Take the next step while momentum is high."}</p>
        <ReservationNotice slug={slug} reservation={reservation} />
        <button type="button" onClick={() => (owned?.pdf ? downloadReport(evaluationId) : openCheckout("PDF_REPORT", { evaluationId }))} className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm font-semibold text-white" data-testid="cta-download-pdf">
          <FileDown className="h-4 w-4" /> {owned?.pdf ? "Download PDF Report" : "Download Full 20-Criteria PDF Report"}
        </button>
        <button type="button" disabled={!!owned?.dfy} onClick={() => openCheckout("DFY_WEBSITE_LAUNCH", { evaluationId })} className="iv-cta inline-flex w-full items-center justify-center gap-2 rounded-full px-4 py-2 text-sm font-bold text-white disabled:opacity-60" data-testid="cta-launch-website">
          <Rocket className="h-4 w-4" /> {owned?.dfy ? "Production Launch Purchased" : "Launch Production Website"}
        </button>
      </aside>
    </section>
  );
};
